/* Visit detail — the review workspace.
   Visual reference: docs/design/visit-detail.html. Normative rules: docs/design/spec/.

   This is spec/03 archetype C, and it replaced a nine-tab record viewer. The reframe: this screen
   is a DECISION tool, not a record browser. The old page advertised everything across nine tabs
   while hiding the handful of records that actually needed a verdict, and put the verdict buttons
   above the evidence needed to reach it.

   Shape: sticky command bar (breadcrumb + queue stepping + the visit verdict) → compact identity
   strip → "Needs your attention" queue → section rail → ONE mounted section.

   ⚠ ONE SECTION IS MOUNTED AT A TIME, and that is not an optimisation to trade away. Nine live
   sections mean nine requests and a DOM that scales with the whole visit — price monitoring is
   hundreds of rows, a survey thousands of answers, and every photo report is an image request.
   The rail still shows every count and every "work waiting" dot because those come from the
   summary, so the supervisor sees WHERE the work is without loading any of it.

   Requests: ONE on open — `GET visits/{id}/?summary=1`, which carries the visit plus per-section
   counts, pending counts and the queue rows (see `collect_visit_summary` in picsell-api). Each
   section then fetches its own endpoint the first time it is opened, once, and is cached. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Alert as AntAlert, Avatar, Button, Card, Flex, Image, Popover, Skeleton, Space, Tooltip, Typography } from "antd";
import {
  AuditOutlined,
  CarryOutOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  CompassOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
  ExportOutlined,
  FileImageOutlined,
  FileTextOutlined,
  HistoryOutlined,
  LeftOutlined,
  OrderedListOutlined,
  PhoneOutlined,
  RightOutlined,
  ShopOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import RejectTaskModal from "../../component/RejectTaskModal";
import ReviewModal from "../../component/ReviewModal";
import { SIDE_NAV, VISIT_PAGE } from "../../config/lang";
import { call, getAbortable, isOk } from "../../util/action";
import { photosOf, verdictOf } from "../../util/answer-fields";
import { formatDuration } from "../../util/duration";
import { fill, translate } from "../../util/util";

import { QUEUE_STORE, buildQuery, searchToState } from "./List";
import { RecordModal, RejectModal, StatusTag, TYPE_SECTION, dash, isActive, postApproval, statusOf } from "./parts";
import {
  Delta,
  GpsSection,
  LogSection,
  OrderStatusTag,
  OrdersSection,
  PaymentsSection,
  PriceSection,
  ReportsSection,
  ReturnsSection,
  SurveysSection,
  TasksSection,
  VerdictButtons,
  formatDate,
  formatDateTime,
  formatTime,
  fullName,
  money,
} from "./sections";
import "./visit-detail.scss";

/* One entry per section. `count` and `pending` are read from the summary, never from the section
   payload — that is what lets the rail be complete before anything is loaded. `skeleton` reserves
   roughly the height the loaded section will take, so switching does not collapse the page under
   the reader. */
const SECTIONS = [
  { key: "orders", labelKey: "orders_tab_name", icon: <FileTextOutlined />, url: id => `visits/${id}/orders_in_visit/`, skeleton: 6, orders: true },
  { key: "returns", labelKey: "returns_tab_name", icon: <FileTextOutlined />, url: id => `visits/${id}/returns_in_visit/`, skeleton: 5, orders: true },
  { key: "payments", labelKey: "payments_tab_name", icon: <FileTextOutlined />, url: id => `visits/${id}/payments_in_visit/`, skeleton: 5, orders: true },
  /* `paged` sections hold ONE page of a longer list and take the page in their url. The two that
     do were both showing page 1 of N with nothing saying so — reports had `page=1` hardcoded, and
     price fetched 200 at a time while its toolbar reported the loaded rows as the total. */
  /* `pageSize` without `sizes`: Outforz fixes the page at 20 (`picsell_photo_report`) and the proxy
     forwards only `page`, so the size is a fact, not a choice to offer. Keep the two in step —
     here it keys the per-page cache, and a wrong value would cache two pages under one key. */
  {
    key: "reports",
    labelKey: "zvit_tab_name",
    icon: <FileImageOutlined />,
    url: (id, page) => `visits/${id}/photos_in_visit?page=${page}`,
    skeleton: 8,
    paged: true,
    pageSize: 20,
  },
  { key: "tasks", labelKey: "task_tab_name", icon: <CarryOutOutlined />, url: id => `visits/${id}/tasks_in_visit/`, skeleton: 7 },
  {
    key: "price",
    labelKey: "monitoring_tab_name",
    icon: <AuditOutlined />,
    url: (id, page, size) => `visits/${id}/price_monitoring_in_visit/?page=${page}&page_size=${size}`,
    skeleton: 10,
    paged: true,
    sizes: [50, 100, 200],
  },
  { key: "surveys", labelKey: "surveys_tab_name", icon: <OrderedListOutlined />, url: id => `visits/${id}/surveys_in_visit/`, skeleton: 12 },
  /* The track rides on the visit itself (`visit.meta`), so this one has nothing to fetch. */
  { key: "gps", labelKey: "gps_track", icon: <CompassOutlined />, url: null, skeleton: 8 },
  { key: "log", labelKey: "log_tab_name", icon: <HistoryOutlined />, url: id => `visits/${id}/events/`, skeleton: 6 },
];

/* A section's cache entry is per PAGE, so that changing page fetches without dropping the page the
   reader came from — and coming back to it costs nothing. An unpaged section is always `:1:0`. */
const cacheKey = (key, page, size) => `${key}:${page}:${size}`;
const pageOf = (paging, section) => (section?.paged ? paging[section.key] || { page: 1, size: section.pageSize || section.sizes[0] } : { page: 1, size: 0 });

const VISIT_STATUS = { approved: "approved", rejected: "rejected", pending: "pending", active: "active" };
const visitStatus = visit =>
  isActive(visit)
    ? VISIT_STATUS.active
    : visit?.is_approved === true
    ? VISIT_STATUS.approved
    : visit?.is_approved === false
    ? VISIT_STATUS.rejected
    : VISIT_STATUS.pending;

/* The review session's frozen visit order — `{ key, ids }`, one queue at a time. The list clears it
   on every row click, so each walk into a visit starts a fresh snapshot. Storage can throw
   (private window, blocked site data); without it the page just re-fetches the ids. */
const readQueue = () => {
  try {
    return JSON.parse(sessionStorage.getItem(QUEUE_STORE));
  } catch (error) {
    return null;
  }
};
const writeQueue = value => {
  try {
    sessionStorage.setItem(QUEUE_STORE, JSON.stringify(value));
  } catch (error) {
    /* A snapshot that cannot be kept still works for this mount. */
  }
};

const initialsOf = name =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map(word => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const View = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";
  const t = key => translate(VISIT_PAGE, key, lang);

  const [visit, setVisit] = useState(null);
  const [summary, setSummary] = useState({ counts: {}, pending: {}, pending_items: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  /* Section payloads, keyed by `<section>:<page>:<size>`. Cached so returning to a section — or to
     a page of it — costs nothing, but only the ACTIVE one is ever rendered, so the cache holds
     data, never DOM. The paging state is separate so a page change invalidates nothing else. */
  const [sectionData, setSectionData] = useState({});
  const [paging, setPaging] = useState({});
  const loadingSections = useRef(new Set());

  const [record, setRecord] = useState(null);
  /* Photo reports get their own modal (the one /photo-review uses), which steps the loaded page —
     hence `{ items, index }` rather than a single record. */
  const [report, setReport] = useState(null);
  const [reject, setReject] = useState(null);

  /* `?i=` marks a visit opened from the list, i.e. one that has a queue to step through. */
  const params = new URLSearchParams(location.search);
  const fromQueue = params.has("i");

  /* Orders / returns / payments only exist for companies that bought the feature. */
  const sections = useMemo(() => {
    const hasOrders = Boolean(me?.company?.features?.includes("orders"));
    return SECTIONS.filter(section => hasOrders || !section.orders);
  }, [me]);

  const [active, setActive] = useState(() => params.get("s") || null);

  /* The list's filters ARE its query string, and the row click hands the whole thing over. Going
     back to a bare `/visit` therefore reads as "no filters" and reseeds the county + default
     period — every filter silently lost. Carry the search back, as `/answers/:id` does. */
  const backTo = `/visit${location.search ? location.search : ""}`;

  /* ------------------------------------------------------------------ load */

  const loadVisit = useCallback(
    signal =>
      getAbortable(`visits/${id}/?summary=1`, signal).then(response => {
        if (!response?.id) return null;
        setVisit(response);
        if (response.summary) setSummary(response.summary);
        return response;
      }),
    [id]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    /* A different visit invalidates every cached section — this is also the reset that makes
       queue stepping work without remounting the page. */
    setSectionData({});
    setPaging({});
    loadingSections.current = new Set();
    loadVisit(controller.signal)
      .then(() => setLoading(false))
      .catch(error => {
        if (error.name !== "AbortError") setLoading(false);
      });
    return () => controller.abort();
  }, [loadVisit]);

  /* Open on the first section that has work waiting, so the page lands where the supervisor is
     needed rather than on whatever happens to be first. */
  useEffect(() => {
    if (active || loading) return;
    const withWork = sections.find(section => summary.pending?.[section.key]);
    setActive((withWork || sections[0])?.key || null);
  }, [active, loading, sections, summary]);

  /* The open section rides in the URL, so a reload or a shared link lands in place. */
  useEffect(() => {
    if (!active) return;
    const next = new URLSearchParams(location.search);
    if (next.get("s") === active) return;
    next.set("s", active);
    history.replace({ search: next.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  /* One request per section PAGE, the first time it is asked for.
     ⚠ `getAbortable`, not `call()`: `call` hands `{results: [], count: 0}` to the callback on any
     5xx, and caching that rendered an OUTAGE as "nothing here" under a pending counter
     (PICSELL-1581 #21). A failure is cached as a marker the body renders as an error + Retry. */
  const fetchSection = (section, page, size, then) => {
    const key = cacheKey(section.key, page, size);
    if (loadingSections.current.has(key)) return;
    loadingSections.current.add(key);
    getAbortable(section.url(id, page, size))
      .then(response => {
        loadingSections.current.delete(key);
        setSectionData(prev => ({ ...prev, [key]: response ?? null }));
        if (then) then(response);
      })
      .catch(() => {
        loadingSections.current.delete(key);
        setSectionData(prev => ({ ...prev, [key]: { __error: true } }));
      });
  };

  useEffect(() => {
    if (!active || !visit) return;
    const section = sections.find(entry => entry.key === active);
    if (!section?.url) return;
    const { page, size } = pageOf(paging, section);
    if (sectionData[cacheKey(active, page, size)] !== undefined) return;
    fetchSection(section, page, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, visit, sections, sectionData, paging, id]);

  /* Retry after a failed section fetch: dropping the cache entry re-arms the effect above. */
  const retrySection = key => setSectionData(prev => ({ ...prev, [key]: undefined }));

  /* The rail sticks below the command bar, whose height changes when it wraps. Publish the
     measured heights rather than hardcoding numbers that silently drift (spec/03). */
  const barRef = useRef(null);
  const railRef = useRef(null);
  useEffect(() => {
    const publish = () => {
      const root = document.documentElement;
      if (barRef.current) root.style.setProperty("--vdbar-h", `${Math.round(barRef.current.getBoundingClientRect().height)}px`);
      if (railRef.current) root.style.setProperty("--vdrail-h", `${Math.round(railRef.current.getBoundingClientRect().height)}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    if (barRef.current) observer.observe(barRef.current);
    if (railRef.current) observer.observe(railRef.current);
    return () => observer.disconnect();
  }, [loading]);

  /* ------------------------------------------------------- queue stepping */

  /**
   * Step to the neighbouring visit in the FILTERED list, as it stood when the review began.
   *
   * ⚠ The order is a SNAPSHOT, never the live list. A verdict takes a visit out of a
   * `status=pending` list and the live list closes up behind it, so stepping by position in it
   * skipped visits, stuck at the end, and "previous" opened a visit nobody had reviewed
   * (PICSELL-1629). `visits/ids/` returns the list's ordered ids through the same query the list
   * ran; the page keeps them for the session, so a step is an array lookup with no request, the
   * counter never moves, and a decided visit stays reachable showing its new status.
   *
   * The snapshot is keyed by the list's query (paging aside), so a reload reuses it, and the
   * position is looked up by id rather than trusted from `?i=`.
   */
  const queueKey = useMemo(() => (fromQueue ? buildQuery({ ...searchToState(location.search, me), page: 1, size: 1 }) : null), [
    fromQueue,
    location.search,
    me,
  ]);
  const [queue, setQueue] = useState(null);
  useEffect(() => {
    if (!queueKey) {
      setQueue(null);
      return undefined;
    }
    const stored = readQueue();
    if (stored?.key === queueKey && Array.isArray(stored.ids)) {
      setQueue(stored.ids);
      return undefined;
    }
    const controller = new AbortController();
    getAbortable(`visits/ids/?${queueKey}`, controller.signal)
      .then(ids => {
        if (!Array.isArray(ids)) return;
        writeQueue({ key: queueKey, ids });
        setQueue(ids);
      })
      /* No snapshot, no stepper — the arrows are hidden rather than guessing. */
      .catch(() => {});
    return () => controller.abort();
  }, [queueKey]);

  const queuePos = queue ? queue.indexOf(String(id)) + 1 : 0;
  const stepVisit = useCallback(
    delta => {
      const target = queuePos ? queue[queuePos - 1 + delta] : null;
      if (!target) return;
      const search = new URLSearchParams(location.search);
      search.set("i", String(queuePos + delta));
      /* Keep the open section across the step: a reviewer walking a queue is usually looking at
         the same kind of evidence in each visit. */
      history.push(`/visit/${target}?${search.toString()}`);
    },
    [queue, queuePos, history, location.search]
  );

  useEffect(() => {
    const onKey = event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = (event.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || event.target.isContentEditable) return;
      /* A modal owns the keyboard while it is open. */
      if (document.querySelector(".ant-modal-wrap:not([style*='display: none'])")) return;
      if (event.key === "[") {
        event.preventDefault();
        stepVisit(-1);
      }
      if (event.key === "]") {
        event.preventDefault();
        stepVisit(1);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [stepVisit]);

  /* --------------------------------------------------------- moderation */

  /** Approve/reject one record, then repaint only what changed: the summary (counts + queue) and
      the section the record belongs to. Anything that repaints the whole page loses the
      reviewer's place in it. */
  /** The repaint after a verdict lands, wherever it was posted from — `RejectTaskModal` posts its
      own `approvals/` row (after the corrective task), so it needs this half without the POST. */
  const afterVerdict = (item, isApproved, { toast = true } = {}) => {
    const section = TYPE_SECTION[item.type];
    /* Every cached PAGE of that section goes, not just the one on screen: a verdict changes the
       record's status wherever it was loaded from, and the count in the head above it. */
    const stale = key => key.startsWith(`${section}:`);
    setSectionData(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => !stale(key))));
    loadingSections.current = new Set([...loadingSections.current].filter(key => !stale(key)));
    loadVisit();
    /* The record has been decided, so the modal that was showing it has nothing left to say. */
    setRecord(null);
    /* The photo modal instead moves on to the next photo, as /photo-review's does — closing it sent
       the reviewer back to the grid to find their place (PICSELL-1626 #7). It holds a snapshot of
       the page the refetch above replaces, so the decided item is patched in that snapshot too, or
       stepping back to it would show the old verdict. A reject reaches here after the reject modal
       closes; `report` survived it (the photo modal is unmounted, not cleared, while rejecting). */
    setReport(prev => {
      if (!prev || item.type !== "report") return null;
      const items = prev.items.map(entry =>
        String(entry.picsell_info?.object_id ?? entry.id) === String(item.object_id)
          ? { ...entry, approval: { ...(entry.approval || {}), is_approved: isApproved } }
          : entry
      );
      return { items, index: (prev.index + 1) % items.length };
    });
    /* A record's verdict is not the visit's verdict — they must never share a message, or
       approving one photo reads as approving the whole visit. */
    if (toast) Alert.success(t(isApproved ? "record_approved" : "record_rejected"), { position: "top-right" });
  };

  const moderate = (item, isApproved, comment = "") => {
    setBusy(true);
    postApproval({ objectId: item.object_id, objectType: item.type, isApproved, comment }, ok => {
      setBusy(false);
      if (!ok) return;
      afterVerdict(item, isApproved);
    });
  };

  /* The whole selection in ONE request — `approvals/bulk_approval/` shares a comment across the
     batch, which is exactly the shape bulk carries (PICSELL-1581 #1): bulk reject takes one
     reason and raises no corrective tasks; that flow stays per-record. */
  const bulkModerate = (answerIds, isApproved, comment, done) => {
    if (!answerIds.length) return;
    setBusy(true);
    call(
      {
        method: "POST",
        url: "approvals/bulk_approval/",
        post: { object_ids: answerIds, object_type: "answer", is_approved: isApproved, comment: comment || "" },
      },
      response => {
        setBusy(false);
        const ok = isOk(response);
        if (ok) {
          afterVerdict({ type: "answer" }, isApproved, { toast: false });
          Alert.success(fill(t(isApproved ? "records_approved" : "records_rejected"), { n: answerIds.length }), { position: "top-right" });
        }
        if (done) done(ok);
      }
    );
  };

  const setVisitApproval = value => {
    if (isActive(visit)) {
      Alert.warning(t("visit_active_no_verdict"), { position: "top-right" });
      return;
    }
    if (visit?.is_approved === value) {
      Alert.warning(t(value ? "already_approve_visit_msg" : "already_reject_visit_msg"), { position: "top-right" });
      return;
    }
    setBusy(true);
    call({ method: "PATCH", url: `visits/${id}/`, post: { is_approved: value, approved_by: me.id } }, response => {
      setBusy(false);
      if (!response) return;
      setVisit(prev => ({ ...prev, is_approved: value, approved_by: me }));
      Alert.success(t(value ? "approve_visit_msg" : "reject_visit_msg"), { position: "top-right" });
    });
  };

  const copyLink = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(`${window.location.origin}/visit/${id}`).catch(() => {});
    Alert.success(t("link_copied"), { position: "top-right" });
  };

  /* ------------------------------------------------------------- render */

  if (loading && !visit) {
    return (
      <div className="shell-content vd">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }
  if (!visit) return <div className="shell-content vd">{t("empty_title")}</div>;

  const status = visitStatus(visit);
  const point = visit.point || {};
  const merchandiser = fullName(visit.user);
  const activeSection = sections.find(section => section.key === active);
  const pendingItems = summary.pending_items || [];

  const openRecord = next => setRecord(next);

  /* A task answer in the queue opens the SAME record the Tasks section opens — description, type,
     author, deadline, every photo, the verdict and the way out to /task/:id (PICSELL-1626 #6). The
     summary row carries none of that, so it comes from the Tasks section's own payload: cached if
     that section was visited, otherwise fetched once and cached for it. */
  const tasksKey = cacheKey("tasks", 1, 0);
  const taskRowFor = (rows, item) => rowsOfResponse(rows).find(row => row.answer?.id === item.object_id);
  const openQueueTask = item => {
    const show = response => {
      const row = taskRowFor(response, item);
      if (row) setRecord(taskRecord(row, lang, verdictActionsFor(lang, moderate, setReject), taskId => history.push(`/task/${taskId}`)));
      else if (item.task_id) history.push(`/task/${item.task_id}`);
    };
    const cached = sectionData[tasksKey];
    if (cached && !cached.__error) return show(cached);
    const section = sections.find(entry => entry.key === "tasks");
    if (section) fetchSection(section, 1, 0, show);
  };

  /* The active section's page, and the pager it hands down. A size change resets to page 1 —
     "page 7 of 25-row pages" is not page 7 of 200-row pages. */
  const activePage = pageOf(paging, activeSection);
  const pager = activeSection?.paged
    ? {
        page: activePage.page,
        size: activePage.size,
        sizes: activeSection.sizes,
        labels: { perPage: t("rows_per_page"), range: t("range_of") },
        onPage: next => setPaging(prev => ({ ...prev, [activeSection.key]: { ...activePage, page: next } })),
        onSize: next => setPaging(prev => ({ ...prev, [activeSection.key]: { page: 1, size: next } })),
      }
    : null;

  return (
    <div className="shell-content vd">
      {/* Sticky command bar. `top: var(--header-h); z-index: 20` — the app bar owns 0 / 30, and a
          strip that ties with it wins by DOM order and covers its controls (spec/03). */}
      <Flex ref={barRef} className="vd-bar" align="center" gap="middle" wrap>
        <Flex className="vd-bar__crumb" align="center" gap={6}>
          <Typography.Link className="vd-crumb__parent" onClick={() => history.push(backTo)}>
            {translate(SIDE_NAV, "visit", lang)}
          </Typography.Link>
          <RightOutlined className="vd-crumb__sep" />
          <Typography.Text strong className="vd-num">
            #{String(id).slice(0, 8)}
          </Typography.Text>
          <StatusTag status={status} lang={lang} />
        </Flex>

        {queue?.length > 1 && queuePos ? (
          <Flex className="vd-bar__step" align="center" gap={2}>
            <Button
              type="text"
              size="small"
              icon={<LeftOutlined />}
              disabled={queuePos <= 1}
              aria-label={t("prev_visit")}
              title={`${t("prev_visit")} [`}
              onClick={() => stepVisit(-1)}
            />
            <Typography.Text type="secondary" className="vd-num vd-bar__pos">
              {fill(t("position_of"), { i: queuePos, n: queue.length })}
            </Typography.Text>
            <Button
              type="text"
              size="small"
              icon={<RightOutlined />}
              disabled={queuePos >= queue.length}
              aria-label={t("next_visit")}
              title={`${t("next_visit")} ]`}
              onClick={() => stepVisit(1)}
            />
          </Flex>
        ) : null}

        <span className="vd-spacer" />

        <Space size={8} wrap>
          {/* An open visit takes no verdict. The tooltip sits on the wrapping `<Space>` because a
              disabled button swallows pointer events. */}
          <Tooltip title={isActive(visit) ? t("visit_active_no_verdict") : null}>
            <Space size={8}>
              <Button className="vd-btn--ok" icon={<CheckCircleOutlined />} loading={busy} disabled={isActive(visit)} onClick={() => setVisitApproval(true)}>
                {t("approv_btn")}
              </Button>
              <Button danger icon={<CloseCircleOutlined />} loading={busy} disabled={isActive(visit)} onClick={() => setVisitApproval(false)}>
                {t("reject_btn")}
              </Button>
            </Space>
          </Tooltip>
          <Tooltip title={t("copy_link")}>
            <Button type="text" icon={<CopyOutlined />} aria-label={t("copy_link")} onClick={copyLink} />
          </Tooltip>
          <Tooltip title={t("show_on_map")}>
            <Button type="text" icon={<EnvironmentOutlined />} aria-label={t("show_on_map")} onClick={() => setActive("gps")} />
          </Tooltip>
        </Space>
      </Flex>

      {/* Identity strip — who and where, in one line (it replaced a hero card). */}
      <Card className="vd-id" variant="outlined">
        <Flex align="center" gap="middle" wrap>
          <span className="vd-id__shot">
            {point.image ? <img src={point.image} alt="" onError={event => (event.target.style.display = "none")} /> : <ShopOutlined />}
          </span>
          <Flex vertical flex="1 1 260px" className="vd-id__main">
            <Typography.Text strong className="vd-id__t">
              {[point.locality?.name, point.name].filter(Boolean).join(" · ")}
            </Typography.Text>
            <Flex className="vd-id__sub" align="center" gap="small" wrap>
              <span>
                <EnvironmentOutlined /> {point.address}
              </span>
              <span>
                <ClockCircleOutlined /> {formatDateTime(visit.start, lang)}
              </span>
              <span>
                <FieldTimeOutlined /> {durationOf(visit, lang)}
              </span>
              <span>
                <CompassOutlined /> {t(visit.is_planned ? "on_route" : "off_route")}
              </span>
            </Flex>
          </Flex>
          <Flex align="center" gap="small" className="vd-id__who">
            <Avatar size={40} style={{ fontSize: 16 }} src={visit.user?.photo || null} className="vd-id__av">
              {initialsOf(merchandiser)}
            </Avatar>
            <Flex vertical>
              <Typography.Text strong>{merchandiser || dash}</Typography.Text>
              <Typography.Text type="secondary" className="vd-id__role">
                {t("merchandiser")}
              </Typography.Text>
              {visit.user?.phone_number ? (
                <Typography.Text type="secondary" className="vd-id__phone">
                  <PhoneOutlined /> {visit.user.phone_number}
                </Typography.Text>
              ) : null}
            </Flex>
          </Flex>
        </Flex>
      </Card>

      {/* The actual work: everything still awaiting this supervisor's decision. */}
      <Card className={`vd-q ${pendingItems.length ? "vd-q--work" : "vd-q--done"}`} variant="outlined">
        <Flex className="vd-q__h" align="center" gap="small">
          {pendingItems.length ? <ClockCircleOutlined /> : <CheckCircleOutlined />}
          {t(pendingItems.length ? "needs_attention" : "all_reviewed")}
          <span className="vd-q__n">{pendingItems.length}</span>
        </Flex>
        {pendingItems.length ? (
          pendingItems.map(item => (
            /* Hover (or keyboard focus — the row is tabbable for exactly this) shows the full
               details, and for a task answer the way OUT to /task/:id (PICSELL-1581 #11);
               `task_id` rides on the summary row because `object_id` here is the ANSWER pk. */
            <Popover
              key={`${item.type}-${item.object_id}`}
              trigger={["hover", "focus"]}
              placement="topLeft"
              styles={{ root: { maxWidth: 340 } }}
              content={
                <Flex vertical gap={4}>
                  <Typography.Text strong>{item.title || dash}</Typography.Text>
                  {queueMeta(item) ? <Typography.Text type="secondary">{queueMeta(item)}</Typography.Text> : null}
                  <QueueTaskFacts row={item.type === "answer" ? taskRowFor(sectionData[tasksKey], item) : null} lang={lang} />
                  {item.type === "answer" && item.task_id ? (
                    <Typography.Link onClick={() => history.push(`/task/${item.task_id}`)}>
                      <ExportOutlined /> {t("open_task")}
                    </Typography.Link>
                  ) : null}
                </Flex>
              }>
              <Flex className="vd-q__row" align="center" gap="middle" tabIndex={0}>
                {/* The design puts a photo thumbnail here for reports. The summary is a DB-side
                    aggregate and the photos live in Outforz, so fetching one per row would undo the
                    single-request open — the kind's glyph stands in, which is what the mockup itself
                    does for the kinds that have no photo. */}
                <span className="vd-q__tile">{KIND_ICON[item.type]}</span>
                <Flex
                  vertical
                  flex="1"
                  className={item.type === "answer" ? "vd-q__body vd-q__body--open" : "vd-q__body"}
                  {...(item.type === "answer" ? { component: "button", type: "button", onClick: () => openQueueTask(item) } : {})}>
                  {/* Plain ellipsis, no antd tooltip — the Popover already carries the full title,
                      and two floating layers over one row fight each other. */}
                  <Typography.Text strong ellipsis>
                    {item.title || dash}
                  </Typography.Text>
                  <Typography.Text type="secondary" className="vd-q__m">
                    {queueMeta(item)}
                  </Typography.Text>
                </Flex>
                <span className="vd-q__kind">{t(KIND_LABEL[item.type])}</span>
                <VerdictButtons className="vd-q__btns" lang={lang} busy={busy} onApprove={() => moderate(item, true)} onReject={() => setReject(item)} />
              </Flex>
            </Popover>
          ))
        ) : (
          <Typography.Text type="secondary" className="vd-q__empty">
            {t("all_reviewed_hint")}
          </Typography.Text>
        )}
      </Card>

      {/* Section selector. It carries every "work waiting" dot from the summary, so the supervisor
          can see where the work is without loading any of it. Section SIZES are not shown: a
          number beside a label said how much is in there, never whether any of it needs doing,
          and it cost five COUNTs per open (`collect_visit_summary`). */}
      <div ref={railRef} className="vd-rail" role="tablist" aria-label={t("sections")}>
        {sections.map(section => {
          const pending = summary.pending?.[section.key];
          return (
            <button key={section.key} type="button" role="tab" aria-selected={section.key === active} onClick={() => setActive(section.key)}>
              {t(section.labelKey)}
              {pending ? <span className="vd-dot" title={fill(t("n_pending"), { n: pending })} /> : null}
            </button>
          );
        })}
      </div>

      {activeSection ? (
        <Card className="vd-sec" variant="outlined" role="tabpanel">
          <Flex className="vd-sec__h" align="center" gap="small">
            {activeSection.icon}
            {t(activeSection.labelKey)}
            {summary.pending?.[activeSection.key] ? (
              <span className="vd-sec__pend">{fill(t("n_pending"), { n: summary.pending[activeSection.key] })}</span>
            ) : null}
          </Flex>
          <SectionBody
            section={activeSection}
            data={sectionData[cacheKey(activeSection.key, activePage.page, activePage.size)]}
            pager={pager}
            visit={visit}
            lang={lang}
            onOpenRecord={openRecord}
            onOpenReport={(items, index) => setReport({ items, index })}
            onModerate={moderate}
            onBulkModerate={bulkModerate}
            busy={busy}
            onReject={setReject}
            onRetry={() => retrySection(cacheKey(activeSection.key, activePage.page, activePage.size))}
          />
        </Card>
      ) : null}

      <RecordModal open={Boolean(record)} record={record} lang={lang} onClose={() => setRecord(null)} />
      {/* ⚠ Unmounted while rejecting, not merely covered. The two dialogs are SIBLINGS, so antd's
          `zIndexContext` bumps neither, and at an equal z-index the portal insertion order into
          `<body>` decides — rc-dialog keeps a portal once created, so a reject opened earlier in the
          session put its container first and every later reject rendered BEHIND the photo. Same
          pattern `/photo-review` already proves. `report` survives, so Cancel returns here. */}
      {report && !reject ? (
        <ReportModal report={report} lang={lang} busy={busy} onStep={setReport} onClose={() => setReport(null)} onModerate={moderate} onReject={setReject} />
      ) : null}

      {/* Rejecting a photo report or a task answer asks the merchandiser to fix the shelf — the
          shared corrective-task flow, restored here on PICSELL-1572. Price monitoring keeps the
          plain reason box: it has no point-scoped photo task to raise. */}
      {reject && reject.type !== "pricemonitoringreport" ? (
        <RejectTaskModal
          target={{ objectId: reject.object_id, objectType: reject.type }}
          photo={reject.photo || null}
          point={visit.point || null}
          visitId={visit.id}
          lang={lang}
          onCancel={() => setReject(null)}
          onDone={() => {
            const item = reject;
            setReject(null);
            /* The modal has already toasted its own outcome ("task created" / "rejected"). */
            afterVerdict(item, false, { toast: false });
          }}
        />
      ) : (
        <RejectModal
          open={Boolean(reject)}
          target={reject}
          lang={lang}
          onCancel={() => setReject(null)}
          onConfirm={reason => {
            const item = reject;
            setReject(null);
            setRecord(null);
            moderate(item, false, reason);
          }}
        />
      )}
    </div>
  );
};

const KIND_LABEL = { report: "zvit_tab_name", answer: "task_tab_name", pricemonitoringreport: "monitoring_tab_name" };
const KIND_ICON = { report: <FileImageOutlined />, answer: <CarryOutOutlined />, pricemonitoringreport: <AuditOutlined /> };

/** The queue row's second line: whatever lets the decision be made without opening anything. */
function queueMeta(item) {
  if (item.type === "report") return (item.tags || []).join(" · ");
  if (item.type === "answer") return item.answer || "";
  return (
    <>
      {money(item.price)} → {money(item.sale_price)} <Delta price={item.price} salePrice={item.sale_price} />
    </>
  );
}

function durationOf(visit, lang) {
  return formatDuration(visit?.time_spent, lang) || formatTime(visit?.start, lang);
}

/** The mounted section, or the skeleton that reserves its height while it loads. */
function SectionBody({ section, data, pager, visit, lang, onOpenRecord, onOpenReport, onModerate, onBulkModerate, busy, onReject, onRetry }) {
  const history = useHistory();
  const t = key => translate(VISIT_PAGE, key, lang);

  if (section.key === "gps") return <GpsSection visit={visit} lang={lang} />;
  if (data === undefined) return <Skeleton active paragraph={{ rows: section.skeleton }} title={false} />;
  /* A FAILED fetch, cached as a marker — never rendered as an empty section (#1581 #21). */
  if (data && data.__error) {
    return (
      <AntAlert
        type="error"
        showIcon
        message={t("sec_load_failed")}
        action={
          <Button size="small" onClick={onRetry}>
            {t("retry")}
          </Button>
        }
      />
    );
  }

  const verdictActions = verdictActionsFor(lang, onModerate, onReject);

  switch (section.key) {
    case "orders":
      return <OrdersSection data={data} lang={lang} onOpen={row => openOrder(row, lang, onOpenRecord)} />;
    case "returns":
      return <ReturnsSection data={data} lang={lang} onOpen={row => onOpenRecord(returnRecord(row, lang))} />;
    case "payments":
      return <PaymentsSection data={data} lang={lang} onOpen={row => onOpenRecord(paymentRecord(row, lang))} />;
    case "reports":
      /* The report opens in /photo-review's own modal, not the generic record dialog — same
         records, so the same zoom stage, thumbnail strip, facts rail and keyboard. */
      return <ReportsSection data={data} lang={lang} pager={pager} onOpen={onOpenReport} />;
    case "tasks":
      return (
        <TasksSection
          data={data}
          lang={lang}
          busy={busy}
          onBulkDecide={onBulkModerate}
          onOpen={row => onOpenRecord(taskRecord(row, lang, verdictActions, taskId => history.push(`/task/${taskId}`)))}
        />
      );
    case "price":
      return <PriceSection data={data} lang={lang} pager={pager} onOpen={row => onOpenRecord(priceRecord(row, lang, verdictActions))} />;
    case "surveys":
      return <SurveysSection visitId={visit.id} data={data} lang={lang} />;
    case "log":
      return <LogSection data={data} lang={lang} />;
    default:
      return null;
  }
}

/* ------------------------------------------------------------ record modals */

const rowsOfResponse = response => (Array.isArray(response) ? response : response?.results || []);

/** A record modal's Approve / Reject pair — offered only while the record is still pending.
    `photo` rides into the reject so the corrective task carries the evidence. */
const verdictActionsFor = (lang, onModerate, onReject) => (objectType, objectId, status, title, photo = null) => {
  const t = key => translate(VISIT_PAGE, key, lang);
  return status !== "pending"
    ? []
    : [
        {
          key: "reject",
          tone: "no",
          label: t("reject_btn"),
          icon: <CloseCircleOutlined />,
          onClick: () => onReject({ type: objectType, object_id: objectId, title, photo }),
        },
        {
          key: "approve",
          tone: "ok",
          label: t("approv_btn"),
          icon: <CheckCircleOutlined />,
          onClick: () => onModerate({ type: objectType, object_id: objectId }, true),
        },
      ];
};

/** What the queue's hover adds once the Tasks payload is at hand: who asked, and by when. */
function QueueTaskFacts({ row, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  if (!row) return null;
  const facts = [
    [t("task_tab_createdby_label"), fullName(row.created_by)],
    [t("task_tab_deadline_label"), formatDate(row.end, lang)],
  ].filter(([, value]) => value);
  return facts.map(([label, value]) => (
    <Typography.Text key={label} type="secondary">
      {label}: {value}
    </Typography.Text>
  ));
}

/* Orders carry their line items only on the detail endpoint, so opening one costs a request the
   list never paid for. */
function openOrder(row, lang, onOpenRecord) {
  const t = key => translate(VISIT_PAGE, key, lang);
  call({ method: "GET", url: `excalibur/orders/${row.id}/` }, response => {
    const order = response?.id ? response : row;
    const items = order.items || [];
    onOpenRecord({
      wide: true,
      title: `${t("orders_popup_order_id_title")} ${order.name || ""}`,
      subtitle: [order.counterparty?.name, formatDateTime(order.datetime, lang)].filter(Boolean).join(" · "),
      sections: [
        {
          titleKey: "orders_popup_details_title",
          icon: <FileTextOutlined />,
          rows: [
            [t("orders_popup_counterparty_label"), order.counterparty?.name],
            [t("orders_address_label"), order.counterparty?.address],
            [t("orders_popup_order_type_label"), order.order_type?.name],
            [t("orders_popup_sales_rep_label"), fullName(order.created_by)],
            [t("orders_popup_delivery_time_label"), formatDate(order.delivery_date, lang)],
            [t("orders_popup_order_status_label"), <OrderStatusTag key="s" value={order.status} lang={lang} />],
            [t("orders_popup_comment_label"), order.comments],
          ],
        },
        {
          titleKey: "orders_popup_order_table_label",
          icon: <FileTextOutlined />,
          count: items.length,
          node: (
            /* ⚠ The name is `item.product.name` — `OrderItemSerializer` NESTS the product. Reading a
               flat `item.name` left every row's name blank while its numbers rendered fine.
               The three numeric columns are fixed-width and headed: they were unwidthed spans in a
               flex row, so they never lined up and nothing said what they were.
               ⚠ The per-row sum is `amount × quantity`, the UNDISCOUNTED line total, while the
               `Всього` below carries the order-level discount. Making them agree needs a discounted
               price per line, which `BaseOrderItem` does not model (PICSELL-1572 #9, PICSELL-1574
               #17 — backend, and upstream of that the mobile app: `sendOrder` transmits
               `'amount': p.price`, the BASE price, per line while sending the discount-resolved sum
               as `order_total`, so the payload contradicts itself before picsell-api sees it).

               What is shown instead of a silent contradiction: BOTH numbers, labelled. The gross sum
               of the lines above (which always agrees with them, because it is computed from them)
               and the document's own total. When a discount applies the second is lower, and that
               gap is now visible as a fact rather than looking like a rendering bug.
               ⚠ Nothing here is COMPUTED FROM `order_total`. Across 510 dev orders, 140 of them hold
               the LINE COUNT in that field rather than money and 37 differ from the line sum by more
               than 30%, so any arithmetic on it would print nonsense for roughly a third of orders.
               It is passed through unchanged. */
            <div className="vd-items">
              <Flex className="vd-items__row vd-items__row--head" align="center" gap="small">
                <span className="vd-items__i">{t("orders_popup_order_table_line_no")}</span>
                <span className="vd-items__n">{t("orders_popup_order_table_name")}</span>
                <span className="vd-items__c">{t("orders_popup_order_table_quantity")}</span>
                <span className="vd-items__c">{t("orders_item_price_label")}</span>
                <span className="vd-items__c">{t("orders_item_sum_label")}</span>
              </Flex>
              {items.map((item, index) => (
                <Flex key={item.id ?? index} className="vd-items__row" align="center" gap="small">
                  <span className="vd-items__i vd-num">{index + 1}</span>
                  <span className="vd-items__n">{item.product?.name || item.product?.full_name || dash}</span>
                  <span className="vd-items__c vd-num">{item.quantity}</span>
                  <span className="vd-items__c vd-num">{money(item.amount)}</span>
                  <b className="vd-items__c vd-num">{money((item.amount || 0) * (item.quantity || 0))}</b>
                </Flex>
              ))}
              <Flex className="vd-items__row vd-items__row--total" align="center" gap="small">
                <span className="vd-items__n">{t("orders_lines_total")}</span>
                <b className="vd-items__c vd-num">{money(items.reduce((sum, item) => sum + (Number(item.amount) || 0) * (Number(item.quantity) || 0), 0))}</b>
              </Flex>
              <Flex className="vd-items__row vd-items__row--total" align="center" gap="small">
                <span className="vd-items__n">{t("orders_product_total")}</span>
                <b className="vd-items__c vd-num">{money(order.order_total ?? order.total)}</b>
              </Flex>
            </div>
          ),
        },
      ],
    });
  });
}

function returnRecord(row, lang) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const items = row.items || [];
  return {
    title: `${t("returns_tab_name")} ${row.name || `#${row.id}`}`,
    subtitle: [row.counterparty?.name, formatDateTime(row.datetime, lang)].filter(Boolean).join(" · "),
    sections: [
      {
        titleKey: "returns_tab_name",
        icon: <FileTextOutlined />,
        rows: [
          [t("orders_popup_counterparty_label"), row.counterparty?.name],
          [t("orders_address_label"), row.counterparty?.address],
          [t("orders_return_crated_label"), formatDateTime(row.datetime, lang)],
          [t("orders_physical_return_label"), formatDate(row.date_of_physical_return, lang)],
          [t("orders_status_return_label"), <OrderStatusTag key="s" value={row.status} lang={lang} />],
          [t("orders_popup_comment_label"), row.comments],
        ],
      },
      /* What was actually returned. The modal showed only the four header facts before, so the one
         question it exists to answer — which products came back — had no answer on the page.
         ⚠ NO price or sum column, and that is not an omission to fill in from here: `ReturnItem`
         carries only `product` and `quantity` (the mobile app's `sendReturn` sends exactly those
         two), and a return records no department, so any price would be one of up to three
         department prices guessed and presented as fact. See PICSELL-1572 #12/#13. */
      items.length
        ? {
            titleKey: "orders_popup_order_table_label",
            icon: <FileTextOutlined />,
            count: items.length,
            node: (
              <div className="vd-items">
                <Flex className="vd-items__row vd-items__row--head" align="center" gap="small">
                  <span className="vd-items__i">{t("orders_popup_order_table_line_no")}</span>
                  <span className="vd-items__n">{t("orders_popup_order_table_name")}</span>
                  <span className="vd-items__c">{t("orders_popup_order_table_quantity")}</span>
                </Flex>
                {items.map((item, index) => (
                  <Flex key={item.id ?? index} className="vd-items__row" align="center" gap="small">
                    <span className="vd-items__i vd-num">{index + 1}</span>
                    <span className="vd-items__n">{item.product?.name || item.product?.full_name || dash}</span>
                    <span className="vd-items__c vd-num">{item.quantity}</span>
                  </Flex>
                ))}
              </div>
            ),
          }
        : null,
    ],
  };
}

function paymentRecord(row, lang) {
  const t = key => translate(VISIT_PAGE, key, lang);
  return {
    title: `${t("payments_tab_name")} #${row.id}`,
    subtitle: [row.counterparty?.name, money(row.amount)].filter(Boolean).join(" · "),
    sections: [
      {
        titleKey: "payments_tab_name",
        icon: <FileTextOutlined />,
        rows: [
          [t("orders_popup_counterparty_label"), row.counterparty?.name],
          [t("orders_payment_amount_label"), money(row.amount), { num: true }],
          [t("orders_payment_debt_label"), row.debt?.name_of_the_document],
          [t("orders_product_status_date"), formatDateTime(row.datetime, lang)],
          [t("orders_popup_comment_label"), row.comment],
        ],
      },
    ],
  };
}

/**
 * The photo report, in /photo-review's modal.
 *
 * A thin adapter, not a second modal: `ReviewModal` is prop-driven and already carries the zoom
 * stage, the thumbnail strip, the facts rail and the ← → / A / R keyboard. All this has to do is
 * step the loaded page and translate a verdict into this page's `moderate` / reject-with-reason
 * flow. Stepping wraps, as it does on /photo-review.
 */
function ReportModal({ report, lang, busy, onStep, onClose, onModerate, onReject }) {
  const { items, index } = report;
  const item = items[index];
  if (!item) return null;

  const step = delta => onStep({ items, index: (index + delta + items.length) % items.length });
  /* `picsell_info.object_id` is what `approvals/` keys on — the Outforz content id is not it. */
  const target = {
    type: "report",
    object_id: item.picsell_info?.object_id ?? item.id,
    title: item.additional_info?.title || `#${item.id}`,
    /* `PhotoReview.photo` is the synthesised array; the first frame is what the annotator opens on. */
    photo: item.photo?.[0] || null,
  };

  return (
    <ReviewModal
      item={item}
      position={index + 1}
      total={items.length}
      lang={lang}
      busy={busy}
      onClose={onClose}
      onPrev={() => step(-1)}
      onNext={() => step(1)}
      onApprove={() => onModerate(target, true)}
      onReject={() => onReject(target)}
    />
  );
}

function taskRecord(row, lang, verdictActions, onOpenTask) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const answer = row.answer;
  /* `verdictOf`, not `statusOf(answer.approval)`: `tasks_in_visit` serves the verdict as
     `is_supervised` and no `approval` at all, so every answer read as pending and a decided one
     was offered Approve/Reject again (PICSELL-1626 #6). ⚠ It must be a real status, not null:
     `verdictActions` only renders for the literal "pending", and passing null here is what
     silently killed the modal's Approve/Reject (PICSELL-1581 #4). */
  const status = answer ? verdictOf(answer) : null;
  const tags = (answer?.client_and_tags || []).flatMap(entry => entry.tags || []);
  /* The thumbnails, not just how many there are. This modal replaced a full page that showed the
     photos, and it was rendering `image_count` alone — a number is not evidence to judge on. The
     rows are already in the payload (`AnswerPhotoSerializer`), in either of the two shapes an
     `AnswerPhoto` uses. */
  const photos = photosOf(answer);
  return {
    /* A task's description, photos and answer never fit 720px comfortably (PICSELL-1581 #4). */
    wide: true,
    title: row.title,
    subtitle: [row.template?.name, `#${row.id}`].filter(Boolean).join(" · "),
    status: status || undefined,
    actions: [
      ...(answer?.id ? verdictActions("answer", answer.id, status, row.title, photosOf(answer)[0] || null) : []),
      { key: "open-task", label: t("open_task"), icon: <ExportOutlined />, onClick: () => onOpenTask(row.id) },
    ],
    sections: [
      {
        titleKey: "task_tab_name",
        icon: <CarryOutOutlined />,
        rows: [
          [t("task_tab_info_label"), row.description],
          [t("task_tab_type_label"), row.template?.name],
          [t("task_tab_createdby_label"), fullName(row.created_by)],
          [t("task_tab_deadline_label"), formatDate(row.end, lang)],
        ],
      },
      photos.length
        ? {
            titleKey: "task_tab_count_photo_label",
            icon: <FileImageOutlined />,
            count: photos.length,
            node: (
              <Image.PreviewGroup>
                <Flex gap={4} wrap>
                  {photos.map((src, index) => (
                    <Image key={`${src}-${index}`} src={src} alt="" width={64} className="vd-thumb" />
                  ))}
                </Flex>
              </Image.PreviewGroup>
            ),
          }
        : null,
      {
        titleKey: "task_tab_answer_comment_label",
        icon: <AuditOutlined />,
        rows: [
          [t("task_tab_answer_comment_label"), answer?.answer],
          /* Tri-state: true / false / null — null is "not answered", not "no". */
          [
            t("task_tab_answer_status_label"),
            answer?.boolean_answer === null || answer?.boolean_answer === undefined
              ? null
              : t(answer.boolean_answer ? "task_tab_answer_status_yes_label" : "task_tab_answer_status_no_label"),
          ],
          [t("task_tab_answer_tags_label"), tags.join(", ")],
        ],
      },
    ],
  };
}

function priceRecord(row, lang, verdictActions) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const status = statusOf(row.approval);
  return {
    title: row.product_name || row.product_code,
    subtitle: `${t("monitoring_tab_pcode_label")}: ${row.product_code}`,
    status,
    actions: verdictActions("pricemonitoringreport", row.id, status, row.product_name),
    sections: [
      row.photo_url
        ? {
            node: (
              <div className="vd-photo">
                <img src={row.photo_url} alt="" />
              </div>
            ),
          }
        : null,
      {
        titleKey: "monitoring_tab_name",
        icon: <AuditOutlined />,
        rows: [
          [t("monitoring_tab_price_label"), money(row.price), { num: true }],
          [t("monitoring_tab_sale_price_label"), money(row.sale_price), { num: true }],
          [t("delta"), <Delta key="d" price={row.price} salePrice={row.sale_price} />, { num: true }],
          [t("monitoring_tab_comment_label"), row.comment],
        ],
      },
      approvalBlock(row.approval, lang),
    ],
  };
}

/** Who decided, when and why. Omitted while a record is pending: it would only print "Approved
    by —", which says nothing. */
function approvalBlock(approval, lang) {
  const t = key => translate(VISIT_PAGE, key, lang);
  if (!approval || approval.is_approved === null || approval.is_approved === undefined) return null;
  return {
    titleKey: "approve_reject_label_block",
    icon: <AuditOutlined />,
    rows: [
      [t("approved_by"), fullName(approval.approved_by)],
      [t("approved_at"), formatDateTime(approval.approved_at, lang)],
      approval.is_approved === false ? [t("reject_reason"), approval.comment] : null,
    ],
  };
}

export default View;
